import { useQuery } from '@tanstack/react-query';
import { RadioButton, RadioGroup } from 'app/components/forms/checkboxes-radiobuttons';
import {
  ControlledFormElement,
  FormHorizontalGroup
} from 'app/components/forms/layout';
import { Autocomplete } from 'app/components/inputs/autocomplete/autocomplete';
import { AgeSelector } from 'app/components/inputs/selectors/age-selector';
import { usePopup } from 'app/hooks/utils/popup';
import { GA4 } from 'lib/analytics/gtag';
import { SearchesAPI } from 'lib/api/public/searches-api';
import { SymptomsAPI } from 'lib/api/public/symptoms-api';
import { Age } from 'lib/models/diagnoses/choices/epidemiology/age';
import { FindingType } from 'lib/models/diagnoses/choices/finding-type';
import { Sex } from 'lib/models/diagnoses/choices/sex';
import { SearchQuery } from 'lib/models/searches/query';
import { SymptomItem } from 'lib/models/searches/symptoms';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { LuEyeOff } from 'react-icons/lu';
import { BreakdownPopup } from './components/searches.breakdown';
import { ExcludedDiagnosesList } from './components/searches.excluded.diagnoses';
import { LocalizedFindingAddButton } from './components/searches.localized.add';
import { SelectedLocalizedFindingList } from './components/searches.localized.items';
import { SelectedSymptomList } from './components/searches.symptom.items';
import { SearchQueryProps } from './props';

/**
 * Implementation of the public search page (v1) for the staticsite.
 */
export const PublicSearchV1: React.FC = () => {
  // Enable popups support
  const { enqueuePopup } = usePopup();

  // State management
  const [query, setQuery] = useState<SearchQuery>(
    new SearchQuery({
      age: Age.fromId(40), // Default to 40 years
      sex: Sex.MALE, // Default to male
      symptoms: [],
      localizedFindings: [],
      excludedDiagnoses: []
    })
  );

  // Form data
  const form = useForm<SearchQueryProps>({
    defaultValues: {
      age: Age.fromId(40),
      sex: Sex.MALE,
      symptoms: [],
      localizedFindings: [],
      excludedDiagnoses: []
    }
  });

  // Update search query on form changes
  const watch = form.watch();
  useEffect(() => {
    setQuery(new SearchQuery(watch));
  }, [
    watch.age.id,
    watch.sex.id,
    watch.symptoms
      .map((c) => '[' + c.symptom.id + ']_' + (c.isHighlighted ? '1' : '0'))
      .sort()
      .join('|'),
    watch.localizedFindings
      .map((f) => '[' + f.finding.key() + ']_' + (f.isHighlighted ? '1' : '0'))
      .sort()
      .join('|'),
    watch.excludedDiagnoses
      .map((c) => '[' + c.id + ']')
      .sort()
      .join('|')
  ]);

  const [symptoms, findings, anamneses, localizedFindings, excludedDiagnoses] = useMemo(
    () => [
      query.symptoms.filter((s) => s.symptom.isSymptom()),
      query.symptoms.filter((s) => s.symptom.isFinding()),
      query.symptoms.filter((s) => s.symptom.isAnamnesis()),
      query.localizedFindings,
      query.excludedDiagnoses
    ],
    [query]
  );

  const searchQuery = useQuery({
    queryKey: SearchesAPI.keys.search(query, 25),
    queryFn: () => {
      // Log event to GA4
      GA4.searchEvent();
      // Perform search
      return SearchesAPI.search(query, 0, 25);
    },
    enabled: query && query.canSearch(),
    gcTime: 0,
    staleTime: 0
  });

  return (
    <div>
      <h1>Diagnostic search</h1>
      <div className="page-searches-v3 is-fade-in mt-4">
        <div className="page-searches-v3-columns">
          <div className="column search-inputs">
            <h2 className="mt-0">1. Add patient info</h2>
            <div className="form-layout default-layout">
              <FormHorizontalGroup>
                <Controller
                  key="age"
                  name="age"
                  control={form.control}
                  render={({ field }) => (
                    <ControlledFormElement
                      name={field.name}
                      errors={form.formState.errors}
                    >
                      <h3>Age</h3>
                      <AgeSelector
                        id={field.name}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        style={{ maxWidth: '120px' }}
                        autoFocus
                      />
                    </ControlledFormElement>
                  )}
                />
                <Controller
                  key="sex"
                  name="sex"
                  control={form.control}
                  render={({ field }) => (
                    <ControlledFormElement
                      name={field.name}
                      errors={form.formState.errors}
                    >
                      <h3>Sex</h3>
                      <RadioGroup horizontal>
                        <RadioButton
                          name={field.name}
                          label={Sex.MALE.label}
                          value={Sex.MALE.id}
                          checked={field.value === Sex.MALE}
                          onChange={() => field.onChange(Sex.MALE)}
                        />
                        <RadioButton
                          name={field.name}
                          label={Sex.FEMALE.label}
                          value={Sex.FEMALE.id}
                          checked={field.value === Sex.FEMALE}
                          onChange={() => field.onChange(Sex.FEMALE)}
                        />
                      </RadioGroup>
                    </ControlledFormElement>
                  )}
                />
              </FormHorizontalGroup>
            </div>
            <Controller
              name="symptoms"
              control={form.control}
              render={({ field, fieldState }) => (
                <ControlledFormElement
                  name={field.name}
                  errors={form.formState.errors}
                  variant="unstretched"
                >
                  <h3>Symptoms, clinical findings &amp; anamneses</h3>
                  <Autocomplete
                    name={field.name}
                    isClearOnSelect
                    style={{ width: '100%' }}
                    placeholder="Search..."
                    onSearch={async (query: string) => {
                      const page = await SymptomsAPI.list({ q: query, pageSize: 50 });
                      return page.items.map((s) => ({
                        value: s.id ?? 0,
                        label: s.name,
                        type: s.type.label,
                        isSelectable: field.value.every(
                          (item) => item.symptom.id !== s.id
                        ),
                        object: s
                      }));
                    }}
                    onSelect={(selected) => {
                      const newSymptoms = [
                        ...field.value,
                        new SymptomItem({ symptom: selected })
                      ].sort((a, b) =>
                        a.symptom.name
                          .toLocaleLowerCase()
                          .localeCompare(b.symptom.name.toLocaleLowerCase())
                      );
                      field.onChange(newSymptoms);
                    }}
                  />
                </ControlledFormElement>
              )}
            />
            <h3>Localized findings</h3>
            <div className="search-inputs-localized-findings">
              <LocalizedFindingAddButton form={form} type={FindingType.PAIN} />
              <LocalizedFindingAddButton form={form} type={FindingType.RASH} />
              <LocalizedFindingAddButton form={form} type={FindingType.SWELLING} />
            </div>
          </div>
          {/* CURRENT SELECTS COLUMN */}
          <div className="column current-selections">
            <h2 className="mt-0">2. Current selections</h2>
            {!query.canSearch() && (
              <p className="opacity-40 font-normal">No symptoms have been added yet.</p>
            )}
            {symptoms.length > 0 && (
              <>
                <h3 className="font-light font-small opacity-50">Symptoms</h3>
                <SelectedSymptomList items={symptoms} form={form} />
              </>
            )}
            {findings.length > 0 && (
              <>
                <h3 className="font-light font-small opacity-50">Clinical findings</h3>
                <SelectedSymptomList items={findings} form={form} />
              </>
            )}
            {anamneses.length > 0 && (
              <>
                <h3 className="font-light font-small opacity-50">Anamneses</h3>
                <SelectedSymptomList items={anamneses} form={form} />
              </>
            )}
            {localizedFindings.length > 0 && (
              <>
                <h3 className="font-light font-small opacity-50">Localized findings</h3>
                <SelectedLocalizedFindingList items={localizedFindings} form={form} />
              </>
            )}
            {excludedDiagnoses.length > 0 && (
              <>
                <h3 className="font-light font-small opacity-50">Excluded diagnoses</h3>
                <ExcludedDiagnosesList items={excludedDiagnoses} form={form} />
              </>
            )}
          </div>
          {/* RESULTS COLUMN */}
          <div className="column results">
            <h2 className="mt-0">3. Results</h2>
            {!query.canSearch() && (
              <p className="opacity-40 font-normal">
                Provide at least one symptom to perform a search.
              </p>
            )}
            {searchQuery.isLoading && <p>Loading results...</p>}
            {searchQuery.isError && (
              <p className="text-danger">
                Error loading results: {String(searchQuery.error)}
              </p>
            )}
            {searchQuery.data && searchQuery.data.items.length === 0 && (
              <p className="opacity-40 font-normal">No results found.</p>
            )}
            {searchQuery.data && searchQuery.data.items.length > 0 && (
              <ul>
                {searchQuery.data.items.map((item, index) => (
                  <li key={index}>
                    <span className="diagnosis-rank">{index + 1}.</span>
                    <span className="diagnosis-name">
                      <a href={`/diagnoses/${item.diagnosis.slug}/`} target="_blank">
                        {item.diagnosis.name}
                      </a>
                    </span>
                    <button
                      className="button-exclude-diagnosis button button-xs button-ternary"
                      onClick={() => {
                        const newList = [
                          ...form.getValues().excludedDiagnoses,
                          item.diagnosis
                        ];
                        form.setValue('excludedDiagnoses', newList);
                      }}
                      title="Exclude diagnosis from search results"
                    >
                      <LuEyeOff />
                    </button>
                    <span
                      className="diagnosis-hits"
                      onClick={() => {
                        enqueuePopup({
                          component: () => <BreakdownPopup item={item} query={query} />
                        });
                      }}
                      title="Click to view breakdown of matches for this diagnosis"
                    >
                      {item.hits}
                    </span>
                    <span className="diagnosis-score">{item.score.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
